import React from 'react';
import { useAtom } from 'jotai';
import { View, Text } from '../../lib/react-native-compat';
import { fieldReportAtom, fieldReportDirtyAtom } from './state';
import { sectionByKey } from './sectionDefs';
import PourCards from './PourCards';

// Yes/No/N/A activity checklist at the top of the Quality step. Answers live
// in the single row of the 'quality' JSON section ('Y' / 'N' / 'NA' /
// unanswered), so save/review/sheet ride the generic section pipeline.
// Answering Yes on an activity reveals its follow-up questions (also
// Yes/No/N/A); those answers store on the same row and clear when the
// activity moves off Yes.

const OPTIONS = [
    ['Y', 'Yes'],
    ['N', 'No'],
    ['NA', 'N/A'],
];

const AnswerBoxes = ({ value, onChange, readOnly }) => (
    <View style={{ flexDirection: 'row', columnGap: 16, flexShrink: 0 }}>
        {OPTIONS.map(([v, label]) => (
            <label key={v} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 14, color: '#0F172A', cursor: readOnly ? 'default' : 'pointer' }}>
                <input
                    type="checkbox"
                    checked={value === v}
                    disabled={readOnly}
                    onChange={(e) => onChange(e.target.checked ? v : '')}
                    style={{ width: 18, height: 18, accentColor: '#1D4ED8', cursor: 'inherit' }}
                />
                {label}
            </label>
        ))}
    </View>
);

const QualityChecklist = ({ readOnly }) => {
    const [report, setReport] = useAtom(fieldReportAtom);
    const [, setDirty] = useAtom(fieldReportDirtyAtom);
    const { fields: activities, subQuestions } = sectionByKey.quality;

    if (!report) return null;
    const row = report.sections?.quality?.[0] || {};

    const saveRow = (next) => {
        setReport({
            ...report,
            sections: { ...report.sections, quality: [next] },
        });
        setDirty(true);
    };

    const setActivity = (a, value) => {
        if (readOnly) return;
        const next = { ...row, [a.name]: value };
        if (value !== 'Y') {
            for (const q of subQuestions[a.name] || []) delete next[q.name];
        }
        saveRow(next);
    };

    const setSubAnswer = (name, value) => {
        if (readOnly) return;
        saveRow({ ...row, [name]: value });
    };

    return (
        <View style={{ borderRadius: 12, backgroundColor: '#fff', border: '1px solid #E2E8F0', padding: 14, marginBottom: 14 }}>
            <Text style={{ fontSize: 13, fontWeight: '700', color: '#0F172A', marginBottom: 8 }}>
                Did you perform any activities related to the following?
            </Text>
            {activities.map((a, i) => (
                <View key={a.name} style={{ borderTop: i === 0 ? 'none' : '1px solid #F1F5F9' }}>
                    <View style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', rowGap: 6, paddingTop: 9, paddingBottom: 9 }}>
                        <Text style={{ fontSize: 14, fontWeight: '600', color: '#0F172A', width: 150 }}>{a.label}</Text>
                        <AnswerBoxes value={row[a.name]} onChange={(v) => setActivity(a, v)} readOnly={readOnly} />
                    </View>
                    {row[a.name] === 'Y' && (
                        <View style={{ marginLeft: 24, marginBottom: 10, borderRadius: 8, backgroundColor: '#F8FAFC', borderLeft: '3px solid #94A3B8', paddingTop: 2, paddingBottom: 2, paddingLeft: 12, paddingRight: 12 }}>
                            {(subQuestions[a.name] || []).map((q, j) => (
                                <View
                                    key={q.name}
                                    style={{ flexDirection: 'row', alignItems: 'center', flexWrap: 'wrap', columnGap: 12, rowGap: 6, paddingTop: 8, paddingBottom: 8, borderTop: j === 0 ? 'none' : '1px solid #E2E8F0' }}
                                >
                                    <Text style={{ fontSize: 13, color: '#334155', flex: 1, minWidth: 200 }}>{q.label}</Text>
                                    <AnswerBoxes value={row[q.name]} onChange={(v) => setSubAnswer(q.name, v)} readOnly={readOnly} />
                                </View>
                            ))}
                            {a.name === 'civil' && (
                                <View style={{ borderTop: '1px solid #E2E8F0', paddingTop: 8 }}>
                                    <PourCards readOnly={readOnly} />
                                </View>
                            )}
                        </View>
                    )}
                </View>
            ))}
        </View>
    );
};

export default QualityChecklist;
